<template>
  <div class="channel-create-container">
    <a-spin :spinning="pageLoading" tip="加载中..." class="page-spin">
      <a-row>
        <a-col :span="4" />
        <a-col :span="16">
          <div class="page-header">
            <a-button v-if="pageType === 'update' || pageType === 'test'" type="link" class="page-back-btn" @click="$router.push({ name: 'configuration' })">
              <a-icon type="arrow-left" /> 返回通道列表
            </a-button>
            <span class="page-title">{{ pageType === 'update' ? '编辑' : pageType === 'test' ? '测试' : '创建' }}通道</span>
            <span class="page-subtitle">配置消息通知通道，支持邮件、钉钉、企业微信、Webhook 等多种网关类型</span>
          </div>

          <a-steps :current="currentStep" class="channel-steps">
            <a-step title="选择网关" description="选择通知渠道类型" />
            <a-step title="填写配置" description="配置网关连接参数" />
            <a-step title="关联模板与限流" description="选择消息模板并设置频率限制" />
            <a-step title="测试与完成" description="测试通道可用性并保存" />
          </a-steps>

          <!-- Step 1: 选择网关类型 + 基本信息 -->
          <div v-show="currentStep === 0" class="step-content">
            <a-card :bordered="false" class="form-card">
              <div class="form-item">
                <label class="form-label required">通道名称</label>
                <a-input v-model="title" placeholder="请输入通道名称，如：系统邮件通知" :maxLength="100" />
              </div>
              <div class="form-item">
                <label class="form-label">通道描述</label>
                <a-textarea v-model="description" placeholder="描述该通道的用途" :rows="2" />
              </div>
              <div class="form-item">
                <label class="form-label required">网关类型</label>
                <div class="gateway-cards">
                  <div v-for="(item, key) in siteOptions" :key="key" :class="['gateway-card', { active: site_id === key }]" @click="selectGateway(key)">
                    <div class="gateway-icon">
                      <a-icon v-if="key === 'mail'" type="mail" />
                      <a-icon v-else-if="key === 'dingding'" type="dingding" />
                      <a-icon v-else-if="key === 'dingtalk'" type="notification" />
                      <a-icon v-else-if="key === 'dingrobot'" type="robot" />
                      <a-icon v-else-if="key === 'wechat'" type="wechat" />
                      <a-icon v-else type="link" />
                    </div>
                    <div class="gateway-name">{{ item }}</div>
                    <div class="gateway-check" v-if="site_id === key">
                      <a-icon type="check-circle" theme="filled" />
                    </div>
                  </div>
                </div>
              </div>
            </a-card>
          </div>

          <!-- Step 2: 网关配置详情 -->
          <div v-show="currentStep === 1" class="step-content">
            <a-card :bordered="false" class="form-card">
              <template v-if="site_id === 'mail'">
                <div class="form-item">
                  <label class="form-label required">SMTP 服务器地址</label>
                  <a-input v-model="mail.host" placeholder="如：smtp.exmail.qq.com" />
                </div>
                <a-row :gutter="16">
                  <a-col :span="12">
                    <div class="form-item">
                      <label class="form-label required">端口</label>
                      <a-input v-model="mail.port" type="number" placeholder="如：465" />
                    </div>
                  </a-col>
                  <a-col :span="12">
                    <div class="form-item">
                      <label class="form-label required">用户名</label>
                      <a-input v-model="mail.user" placeholder="邮箱账号" />
                    </div>
                  </a-col>
                </a-row>
                <div class="form-item">
                  <label class="form-label required">密码</label>
                  <a-input v-model="mail.password" type="password" placeholder="邮箱密码或授权码" />
                </div>
                <a-row :gutter="16">
                  <a-col :span="12">
                    <div class="form-item">
                      <label class="form-label">发件人名称</label>
                      <a-input v-model="mail.from_name" placeholder="收件方看到的发件人名称" />
                    </div>
                  </a-col>
                  <a-col :span="12">
                    <div class="form-item">
                      <label class="form-label">发件邮箱</label>
                      <a-input v-model="mail.from_address" placeholder="收件方看到的发件邮箱" />
                    </div>
                  </a-col>
                </a-row>
              </template>

              <template v-else-if="site_id === 'dingding'">
                <div class="form-item">
                  <label class="form-label required">Webhook 地址</label>
                  <a-input v-model="dingding.url" placeholder="钉钉群机器人的 Webhook 地址" />
                </div>
                <div class="form-item">
                  <label class="form-label">加签密钥</label>
                  <a-input v-model="dingding.encrypt_key" type="password" placeholder="安全设置中的加签密钥（可选）" />
                </div>
                <div class="form-item">
                  <label class="form-label">@所有人</label>
                  <a-switch v-model="dingding.is_at_all" />
                  <span class="form-hint">开启后每条消息都会 @所有人</span>
                </div>
              </template>

              <template v-else-if="site_id === 'webhook'">
                <div class="form-item">
                  <label class="form-label required">请求 URL</label>
                  <a-input v-model="webhook.url" placeholder="HTTP 请求地址" />
                </div>
                <div class="form-item">
                  <label class="form-label required">请求方法</label>
                  <a-select v-model="webhook.http_method" style="width: 200px">
                    <a-select-option value="GET">GET</a-select-option>
                    <a-select-option value="POST">POST</a-select-option>
                    <a-select-option value="PUT">PUT</a-select-option>
                    <a-select-option value="DELETE">DELETE</a-select-option>
                  </a-select>
                </div>
                <div class="form-item">
                  <label class="form-label">Content-Type</label>
                  <a-input v-model="webhook.content_type" placeholder="如：application/json" />
                </div>
                <div class="form-item">
                  <label class="form-label">请求头</label>
                  <a-input v-model="webhook.http_header" placeholder='JSON 格式，如：{"Authorization":"Bearer xxx"}' />
                </div>
              </template>

              <template v-else-if="site_id === 'dingtalk'">
                <div class="form-item">
                  <label class="form-label required">Agent ID</label>
                  <a-input v-model="dingtalk.agent_id" placeholder="钉钉应用 Agent ID" />
                </div>
                <div class="form-item">
                  <label class="form-label required">App Key</label>
                  <a-input v-model="dingtalk.app_key" placeholder="钉钉应用 App Key" />
                </div>
                <div class="form-item">
                  <label class="form-label required">App Secret</label>
                  <a-input v-model="dingtalk.app_secret" type="password" placeholder="钉钉应用 App Secret" />
                </div>
              </template>

              <template v-else-if="site_id === 'dingrobot'">
                <div class="form-item">
                  <label class="form-label required">App Key</label>
                  <a-input v-model="dingrobot.app_key" placeholder="钉钉应用 App Key" />
                </div>
                <div class="form-item">
                  <label class="form-label required">App Secret</label>
                  <a-input v-model="dingrobot.app_secret" type="password" placeholder="钉钉应用 App Secret" />
                </div>
                <div class="form-item">
                  <label class="form-label required">Robot Code</label>
                  <a-input v-model="dingrobot.robot_code" placeholder="机器人编码" />
                </div>
                <div class="form-item">
                  <label class="form-label">提醒类型</label>
                  <a-select v-model="dingrobot.remind_type" style="width: 200px">
                    <a-select-option :value="1">应用内 DING</a-select-option>
                  </a-select>
                </div>
              </template>

              <template v-else-if="site_id === 'wechat'">
                <div class="form-item">
                  <label class="form-label required">机器人 Webhook URL</label>
                  <a-input v-model="wechat.url" placeholder="企业微信机器人接入地址" />
                </div>
                <div class="form-item">
                  <label class="form-label">@所有人</label>
                  <a-switch v-model="wechat.is_at_all" />
                </div>
              </template>
            </a-card>
          </div>

          <!-- Step 3: 选择模板 + 限流配置 -->
          <div v-show="currentStep === 2" class="step-content">
            <a-card :bordered="false" class="form-card">
              <div class="form-item">
                <label class="form-label required">消息模板</label>
                <a-spin :spinning="templateLoading">
                  <a-select v-model="template_id" style="width: 100%" placeholder="请选择消息模板">
                    <a-select-option v-for="item in templateList" :key="item.id" :value="item.id">
                      <div>
                        <span>{{ item.name }}</span>
                        <a-tag style="margin-left: 8px" :color="item.type === 'html' ? 'blue' : item.type === 'markdown' ? 'green' : ''">{{ item.type }}</a-tag>
                      </div>
                    </a-select-option>
                  </a-select>
                </a-spin>
              </div>
              <div class="form-item">
                <label class="form-label">重试次数</label>
                <a-input-number v-model="retry_count" :min="0" :max="10" style="width: 200px" />
                <span class="form-hint">发送失败后的最大重试次数，0 表示不重试</span>
              </div>

              <a-divider orientation="left">频率限制</a-divider>
              <div class="form-item">
                <label class="form-label">启用频率限制</label>
                <a-switch v-model="rateLimitEnabled" @change="onRateLimitChange" />
                <span class="form-hint">{{ rateLimitEnabled ? '已开启，超限后按策略处理' : '已关闭，不限制发送频率' }}</span>
              </div>
              <a-alert
                v-if="rateLimitEnabled"
                message="配置通道的请求频率限制，防止短时间内发送过多消息。超过限制后，dingrobot 类型将返回错误，其他类型将缓冲延迟发送。"
                type="info"
                show-icon
                closable
                style="margin-bottom: 16px"
              />
              <a-row :gutter="16" v-if="rateLimitEnabled">
                <a-col :span="10">
                  <div class="form-item">
                    <label class="form-label">限制次数</label>
                    <a-input-number v-model="rate_limit" :min="1" :max="9999" style="width: 100%" placeholder="周期内允许调用次数" />
                  </div>
                </a-col>
                <a-col :span="14">
                  <div class="form-item">
                    <label class="form-label">限制周期</label>
                    <a-select v-model="rate_duration" style="width: 100%" placeholder="选择限制周期">
                      <a-select-option value="1m">1 分钟</a-select-option>
                      <a-select-option value="5m">5 分钟</a-select-option>
                      <a-select-option value="10m">10 分钟</a-select-option>
                      <a-select-option value="30m">30 分钟</a-select-option>
                      <a-select-option value="1h">1 小时</a-select-option>
                      <a-select-option value="6h">6 小时</a-select-option>
                      <a-select-option value="12h">12 小时</a-select-option>
                      <a-select-option value="24h">24 小时</a-select-option>
                    </a-select>
                  </div>
                </a-col>
              </a-row>
              <div class="rate-preview" v-if="rateLimitEnabled && rate_limit > 0">
                <a-icon type="info-circle" />
                当前配置：<strong>每 {{ formatDuration(rate_duration) }} 内最多发送 {{ rate_limit }} 次</strong>
                <template v-if="site_id === 'dingrobot'">，超限后将<strong style="color: #f5222d">拒绝发送并返回错误</strong></template>
                <template v-else>，超限后将<strong>缓冲延迟发送</strong></template>
              </div>
            </a-card>
          </div>

          <!-- Step 4: 测试与完成 -->
          <div v-show="currentStep === 3" class="step-content">
            <a-card :bordered="false" class="form-card">
              <a-alert
                message="配置摘要"
                :description="`网关类型：${siteOptions[site_id]} | 模板：${templateList.find(t => t.id === template_id)?.name || '未选择'} | 限流：${rate_limit > 0 ? formatDuration(rate_duration) + '内' + rate_limit + '次' : '无限制'}`"
                type="success"
                show-icon
                style="margin-bottom: 24px"
              />

              <a-divider orientation="left">消息内容编辑</a-divider>
              <a-alert message="根据模板变量填写消息内容，变量值将替换模板中的占位符" type="info" show-icon style="margin-bottom: 16px" />

              <a-row :gutter="24">
                <!-- 左侧：模板内容 & 变量编辑 -->
                <a-col :span="14">
                  <div class="form-item">
                    <label class="form-label">模板标题</label>
                    <div class="template-display-box">{{ testTemplateTitle || '（未选择模板）' }}</div>
                  </div>
                  <div class="form-item">
                    <label class="form-label">模板正文</label>
                    <div class="template-display-box template-content-box">{{ testTemplateContent || '（未选择模板）' }}</div>
                  </div>

                  <!-- 变量赋值 -->
                  <div v-if="testParsedVars.length > 0" class="var-inputs" style="margin-top: 16px">
                    <a-divider orientation="left" style="font-size: 13px">变量赋值（编辑消息内容）</a-divider>
                    <div v-for="v in testParsedVars" :key="v" class="var-input-item">
                      <span class="var-input-label">{{ v }}</span>
                      <a-input
                        v-model="testVarValues[v]"
                        size="small"
                        :placeholder="varPlaceholders[v] || '请输入' + v"
                        style="flex: 1"
                        @change="debounceTestRender"
                      />
                    </div>
                  </div>
                </a-col>

                <!-- 右侧：渲染预览 & 收件人 & 发送 -->
                <a-col :span="10">
                  <a-card :bordered="false" class="preview-card" title="渲染预览">
                    <template slot="extra">
                      <a-button size="small" type="primary" @click="doTestRender" :loading="rendering"> <a-icon type="reload" /> 刷新预览 </a-button>
                    </template>

                    <a-divider orientation="left" style="font-size: 12px">标题预览</a-divider>
                    <div class="preview-title-box">
                      <div class="preview-title-content">
                        <message-content v-if="testPreviewTitle" mode="inline" :content="testPreviewTitle" />
                        <span v-else class="preview-placeholder">（填写变量后预览）</span>
                      </div>
                    </div>

                    <a-divider orientation="left" style="font-size: 12px">正文预览</a-divider>
                    <div class="preview-content-box">
                      <message-content v-if="testPreviewContent" :content="testPreviewContent" />
                      <span v-else class="preview-placeholder">（填写变量后预览）</span>
                    </div>
                  </a-card>
                </a-col>
              </a-row>

              <a-divider orientation="left">收件人配置</a-divider>
              <a-alert message="填写收件人信息后点击「发送测试消息」，验证通道配置是否正确" type="info" show-icon style="margin-bottom: 16px" />

              <div class="receiver-section">
                <div v-for="(receiver, receiverIndex) in receiver" :key="receiverIndex" class="receiver-card">
                  <div class="receiver-header">
                    <span>收件人 {{ receiverIndex + 1 }}</span>
                    <a-button type="link" size="small" @click="subReceiver(receiverIndex)" v-if="receiver.length > 1"> <a-icon type="delete" /> 移除 </a-button>
                  </div>
                  <div class="receiver-fields">
                    <div class="form-item">
                      <label class="form-label">用户名</label>
                      <a-input v-model="receiver.username" placeholder="接收人用户名" />
                    </div>
                    <div v-if="site_id === 'mail'" class="form-item">
                      <label class="form-label required">邮箱</label>
                      <a-input v-model="receiver.email" placeholder="接收人邮箱地址" />
                    </div>
                    <div v-if="site_id === 'mail'" class="form-item">
                      <label class="form-label">抄送</label>
                      <a-switch v-model="receiver.is_cc" />
                    </div>
                    <div v-if="site_id === 'dingding'">
                      <div class="form-item">
                        <label class="form-label">
                          <a-tooltip title="查看路径：钉钉用户头像 → 更多信息 → 钉钉号"> 钉钉号 <a-icon type="question-circle" /> </a-tooltip>
                        </label>
                        <a-input v-model="receiver.dingtalk_id" placeholder="用户的钉钉号" />
                      </div>
                      <div class="form-item">
                        <label class="form-label">手机号</label>
                        <a-input v-model="receiver.phone" placeholder="用户手机号" />
                      </div>
                    </div>
                  </div>
                </div>
                <a-button type="dashed" block @click="addReceiver" style="margin-top: 8px"> <a-icon type="plus" /> 添加收件人 </a-button>
              </div>

              <div class="test-result" v-if="testStatus">
                <a-alert :type="testStatus === 'success' ? 'success' : 'error'" show-icon>
                  <template slot="message">
                    {{ testStatus === 'success' ? '测试消息发送成功！通道配置正确。' : '测试消息发送失败，请检查配置信息和收件人信息。' }}
                  </template>
                </a-alert>
              </div>

              <div class="step-actions">
                <a-button type="primary" @click="handleTest" :loading="testing"> <a-icon type="experiment" /> 发送测试消息 </a-button>
                <a-button
                  type="primary"
                  ghost
                  @click="finishCreate"
                  v-if="pageType !== 'show' && pageType !== 'test'"
                  :loading="submitting"
                  style="margin-left: 12px"
                >
                  <a-icon type="check" /> {{ pageType === 'update' ? '保存更新' : '创建通道' }}
                </a-button>
              </div>
            </a-card>
          </div>

          <!-- Step 导航 -->
          <div class="step-navigation" v-if="pageType !== 'test'">
            <a-button v-if="currentStep > 0" @click="prevStep" style="margin-right: 12px"> <a-icon type="left" /> 上一步 </a-button>
            <a-button type="primary" v-if="currentStep < 3" @click="nextStep"> 下一步 <a-icon type="right" /> </a-button>
          </div>
        </a-col>
        <a-col :span="4" />
      </a-row>
    </a-spin>
  </div>
</template>

<script>
import { createChannel, getSingleChannel, getTemplateList, getSingleTemplate, renderTemplate, testChannel, updateChannel } from '@/api/notice'
import MessageContent from '@/components/MessageContent'

export default {
  components: { MessageContent },
  data() {
    return {
      currentStep: 0,
      testStatus: null,
      testing: false,
      submitting: false,
      pageLoading: false,
      rendering: false,
      testTemplateTitle: '',
      testTemplateContent: '',
      testParsedVars: [],
      testVarValues: {},
      testPreviewTitle: '',
      testPreviewContent: '',
      testRenderTimer: null,
      varPlaceholders: {
        now: '当前时间（自动填充）',
        level: '日志级别，如 INFO / WARN / ERROR / CRITICAL',
        system: '系统名称，如 erp / wms / noticegateway',
        module: '模块名称，如 order / inventory / user',
        department: '部门名称，如 研发一部 / 运营部',
        message: '通知正文内容',
        title: '自定义标题文本',
        type_id: '事件类型 ID（数字）',
        id: '事件日志 ID',
        create_time: '创建时间（自动填充）',
        update_time: '更新时间（自动填充）'
      },
      title: '',
      description: '',
      template_id: undefined,
      templateLoading: true,
      templateList: [],
      retry_count: 1,
      rate_limit: 3,
      rate_duration: '24h',
      rateLimitEnabled: false,
      mail: {
        host: '',
        port: '',
        user: '',
        password: '',
        from_address: '',
        from_name: ''
      },
      dingding: {
        url: '',
        encrypt_key: '',
        is_at_all: false
      },
      webhook: {
        url: '',
        http_method: '',
        http_header: '{}',
        content_type: ''
      },
      dingtalk: {
        agent_id: '',
        app_key: '',
        app_secret: ''
      },
      dingrobot: {
        app_key: '',
        app_secret: '',
        robot_code: '',
        remind_type: 1
      },
      wechat: {
        url: '',
        is_at_all: false
      },
      pageType: 'create',
      siteOptions: {
        mail: '邮件',
        dingding: '钉钉群通知',
        webhook: 'Webhook',
        dingtalk: '钉钉工作通知',
        dingrobot: '钉钉机器人 DING',
        wechat: '企业微信群通知'
      },
      site_id: 'mail',
      receiver: [
        {
          username: '',
          email: '',
          is_cc: false,
          phone: '',
          dingtalk_id: ''
        }
      ]
    }
  },
  computed: {
    buttontext() {
      if (this.pageType === 'show' || this.pageType === 'test') return ''
      return this.pageType === 'update' ? '更新' : '创建'
    }
  },
  created() {
    this.pageType = this.$route.query.type
    this.id = this.$route.query.id
    if (this.pageType === 'update' || this.pageType === 'show' || this.pageType === 'test') {
      this.pageLoading = true
      getSingleChannel(this.id)
        .then(res => {
          const data = res.data
          this.site_id = data.gateway
          this.title = data.name
          this.description = data.description
          this.retry_count = data.retry_count ?? 1
          this.rate_limit = data.rate_limit ?? 3
          this.rate_duration = data.rate_duration || '24h'
          this.rateLimitEnabled = data.rate_limit > 0
          this.template_id = data.template_id
          if (this.site_id === 'mail') {
            this.mail = { ...this.mail, ...data.setting }
          } else if (this.site_id === 'dingding') {
            this.dingding = { ...this.dingding, ...data.setting }
          } else if (this.site_id === 'webhook') {
            this.webhook = { ...this.webhook, ...data.setting }
            this.webhook.http_header = data.setting.http_header ? JSON.stringify(data.setting.http_header) : '{}'
          } else if (this.site_id === 'dingtalk') {
            this.dingtalk = { ...this.dingtalk, ...data.setting }
          } else if (this.site_id === 'dingrobot') {
            this.dingrobot = { ...this.dingrobot, ...data.setting }
          } else if (this.site_id === 'wechat') {
            this.wechat = { ...this.wechat, ...data.setting }
          }
          this.gatewayChange(true)
          if (this.pageType === 'test') {
            this.currentStep = 3
            this.$nextTick(() => {
              this.loadTestTemplate()
            })
          }
        })
        .finally(() => {
          this.pageLoading = false
        })
    } else {
      this.gatewayChange()
    }
  },
  methods: {
    selectGateway(key) {
      this.site_id = key
      this.gatewayChange()
    },
    gatewayChange(preserveTemplate) {
      this.templateLoading = true
      if (!preserveTemplate) {
        this.template_id = undefined
      }
      getTemplateList({ gateway: this.site_id, page_size: 500 })
        .then(res => {
          this.templateList = res.data.list
          this.templateLoading = false
        })
        .catch(() => {
          this.templateList = []
          this.templateLoading = false
        })
    },
    formatDuration(duration) {
      const map = {
        '1m': '1 分钟',
        '5m': '5 分钟',
        '10m': '10 分钟',
        '30m': '30 分钟',
        '1h': '1 小时',
        '6h': '6 小时',
        '12h': '12 小时',
        '24h': '24 小时'
      }
      return map[duration] || duration
    },
    nextStep() {
      if (this.currentStep === 0) {
        if (!this.title) {
          this.$message.warning('请输入通道名称')
          return
        }
        if (!this.site_id) {
          this.$message.warning('请选择网关类型')
          return
        }
      }
      if (this.currentStep === 1) {
        const valid = this.validateGatewayConfig()
        if (!valid) return
      }
      if (this.currentStep === 2) {
        if (!this.template_id) {
          this.$message.warning('请选择消息模板')
          return
        }
      }
      this.currentStep++
      if (this.currentStep === 3) {
        this.loadTestTemplate()
      }
    },
    prevStep() {
      this.currentStep--
    },
    validateGatewayConfig() {
      if (this.site_id === 'mail') {
        if (!this.mail.host || !this.mail.port || !this.mail.user || !this.mail.password) {
          this.$message.warning('SMTP 服务器地址、端口、用户名和密码为必填项')
          return false
        }
        if (isNaN(parseInt(this.mail.port))) {
          this.$message.warning('端口必须为数字')
          return false
        }
        this.mail.port = parseInt(this.mail.port)
      }
      if (this.site_id === 'dingding' && !this.dingding.url) {
        this.$message.warning('Webhook 地址为必填项')
        return false
      }
      if (this.site_id === 'webhook' && (!this.webhook.url || !this.webhook.http_method)) {
        this.$message.warning('请求 URL 和请求方法为必填项')
        return false
      }
      if (this.site_id === 'dingtalk' && (!this.dingtalk.agent_id || !this.dingtalk.app_key || !this.dingtalk.app_secret)) {
        this.$message.warning('Agent ID、App Key 和 App Secret 为必填项')
        return false
      }
      if (this.site_id === 'dingrobot' && (!this.dingrobot.app_key || !this.dingrobot.app_secret || !this.dingrobot.robot_code)) {
        this.$message.warning('App Key、App Secret 和 Robot Code 为必填项')
        return false
      }
      if (this.site_id === 'wechat' && !this.wechat.url) {
        this.$message.warning('Webhook URL 为必填项')
        return false
      }
      return true
    },
    getSetting() {
      let webHookHttpHeader = null
      if (this.site_id === 'webhook') {
        try {
          webHookHttpHeader = JSON.parse(this.webhook.http_header)
        } catch (e) {
          return null
        }
      }
      const settingMappings = {
        mail: this.mail,
        dingding: this.dingding,
        webhook: { ...this.webhook, http_header: webHookHttpHeader },
        dingtalk: this.dingtalk,
        dingrobot: this.dingrobot,
        wechat: this.wechat
      }
      return settingMappings[this.site_id]
    },
    onRateLimitChange(enabled) {
      if (!enabled) {
        this.rate_limit = 0
      } else {
        this.rate_limit = 3
        this.rate_duration = '24h'
      }
    },
    getParams() {
      const setting = this.getSetting()
      if (!setting) return null
      return {
        gateway: this.site_id,
        name: this.title,
        template_id: this.template_id,
        description: this.description,
        setting,
        retry_count: this.retry_count,
        rate_limit: this.rateLimitEnabled ? this.rate_limit : 0,
        rate_duration: this.rate_duration
      }
    },
    addReceiver() {
      this.receiver.push({
        username: '',
        email: '',
        is_cc: false,
        phone: '',
        dingtalk_id: ''
      })
    },
    subReceiver(index) {
      if (this.receiver.length > 1) {
        this.receiver.splice(index, 1)
      }
    },
    loadTestTemplate() {
      if (!this.template_id) {
        this.testTemplateTitle = ''
        this.testTemplateContent = ''
        this.testParsedVars = []
        this.testVarValues = {}
        return
      }
      getSingleTemplate(this.template_id)
        .then(res => {
          const data = res.data
          this.testTemplateTitle = data.title || ''
          this.testTemplateContent = data.content || ''
          this.parseTestVars()
          this.doTestRender()
        })
        .catch(() => {
          this.$message.warning('加载模板内容失败')
        })
    },
    parseTestVars() {
      const fullText = this.testTemplateTitle + ' ' + this.testTemplateContent
      const regex = /\{\{\.(\w+)\}\}/g
      const vars = new Set()
      let match
      while ((match = regex.exec(fullText)) !== null) {
        vars.add(match[1])
      }
      this.testParsedVars = Array.from(vars)

      const newVarValues = {}
      this.testParsedVars.forEach(v => {
        newVarValues[v] = this.testVarValues[v] || ''
      })
      this.testVarValues = newVarValues
    },
    debounceTestRender() {
      if (this.testRenderTimer) clearTimeout(this.testRenderTimer)
      this.testRenderTimer = setTimeout(() => {
        this.doTestRender()
      }, 300)
    },
    doTestRender() {
      if (!this.testTemplateContent.trim()) {
        this.testPreviewTitle = ''
        this.testPreviewContent = ''
        return
      }
      this.rendering = true
      const filteredParams = {}
      Object.keys(this.testVarValues).forEach(k => {
        if (this.testVarValues[k] !== '') {
          filteredParams[k] = this.testVarValues[k]
        }
      })
      const params = {
        title: this.testTemplateTitle,
        content: this.testTemplateContent,
        params: filteredParams
      }
      renderTemplate(params)
        .then(res => {
          this.testPreviewTitle = res.data.title || ''
          this.testPreviewContent = res.data.content || ''
        })
        .catch(() => {
          this.testPreviewTitle = this.replaceTestVars(this.testTemplateTitle)
          this.testPreviewContent = this.replaceTestVars(this.testTemplateContent)
        })
        .finally(() => {
          this.rendering = false
        })
    },
    replaceTestVars(text) {
      if (!text) return ''
      let result = text
      this.testParsedVars.forEach(v => {
        const val = this.testVarValues[v]
        const regex = new RegExp(`\\{\\{\\.${v}\\}\\}`, 'g')
        result = result.replace(regex, val || `{{.${v}}}`)
      })
      return result
    },
    handleTest() {
      this.testStatus = null
      if (!this.validateGatewayConfig()) return
      const params = this.getParams()
      if (!params) return

      if (this.site_id === 'mail') {
        const hasValidReceiver = this.receiver.some(r => r.username && r.email)
        if (!hasValidReceiver) {
          this.$message.warning('请至少填写一个收件人的用户名和邮箱')
          return
        }
      }

      params.receiver = this.receiver.filter(obj => Object.values(obj).some(value => !!value))
      params.id = this.id

      const nonEmptyParams = {}
      Object.keys(this.testVarValues).forEach(k => {
        if (this.testVarValues[k] !== '') {
          nonEmptyParams[k] = this.testVarValues[k]
        }
      })
      if (Object.keys(nonEmptyParams).length > 0) {
        params.params = nonEmptyParams
      }

      this.testing = true
      testChannel(params)
        .then(() => {
          this.testStatus = 'success'
          this.$message.success('测试消息发送成功')
        })
        .catch(() => {
          this.testStatus = 'error'
          this.$message.error('测试消息发送失败，请检查配置')
        })
        .finally(() => {
          this.testing = false
        })
    },
    finishCreate() {
      this.testStatus = null
      if (!this.validateGatewayConfig()) return
      const params = this.getParams()
      if (!params) return

      if (!this.template_id) {
        this.$message.warning('请选择消息模板')
        return
      }

      this.submitting = true
      const request = this.pageType === 'update' ? updateChannel(this.id, params) : createChannel(null, params)
      request
        .then(() => {
          this.$message.success(`${this.pageType === 'update' ? '更新' : '创建'}成功`)
          this.$router.push({ path: '/configuration' })
        })
        .catch(() => {
          this.$message.error(`${this.pageType === 'update' ? '更新' : '创建'}失败，请检查信息填写`)
        })
        .finally(() => {
          this.submitting = false
        })
    }
  }
}
</script>

<style scoped>
.channel-create-container {
  padding: 24px;
  min-height: calc(100vh - 64px);
  background: #f0f2f5;
}

.page-spin >>> .ant-spin-container {
  width: 100%;
}

.page-header {
  margin-bottom: 24px;
  text-align: center;
  position: relative;
}

.page-back-btn {
  position: absolute;
  left: 0;
  top: 0;
  font-size: 14px;
  padding: 0;
}

.page-title {
  font-size: 22px;
  font-weight: 600;
  color: #1a1a2e;
  display: block;
}

.page-subtitle {
  font-size: 13px;
  color: #8c8c8c;
  margin-top: 4px;
  display: block;
}

.channel-steps {
  margin-bottom: 32px;
}

.step-content {
  margin-bottom: 24px;
}

.form-card {
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.form-item {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.form-label.required::before {
  content: '*';
  color: #f5222d;
  margin-right: 4px;
}

.form-hint {
  margin-left: 10px;
  font-size: 12px;
  color: #8c8c8c;
}

.gateway-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.gateway-card {
  position: relative;
  padding: 20px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
}

.gateway-card:hover {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
}

.gateway-card.active {
  border-color: #1890ff;
  background: #e6f7ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
}

.gateway-icon {
  font-size: 28px;
  color: #1890ff;
  margin-bottom: 8px;
}

.gateway-card.active .gateway-icon {
  color: #096dd9;
}

.gateway-name {
  font-size: 13px;
  color: #666;
}

.gateway-card.active .gateway-name {
  color: #096dd9;
  font-weight: 500;
}

.gateway-check {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 18px;
  color: #1890ff;
}

.rate-preview {
  margin-top: 12px;
  padding: 10px 14px;
  background: #f6f8fa;
  border-radius: 6px;
  font-size: 13px;
  color: #555;
  line-height: 1.6;
}

.receiver-section {
  margin-bottom: 20px;
}

.receiver-card {
  padding: 12px 16px;
  margin-bottom: 8px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fafafa;
}

.receiver-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #666;
}

.receiver-fields {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0 16px;
}

.receiver-fields .form-item {
  margin-bottom: 12px;
}

.test-result {
  margin: 16px 0;
}

.step-navigation {
  text-align: center;
  margin-top: 24px;
  padding: 16px 0;
}

.template-display-box {
  padding: 12px 16px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
  min-height: 40px;
  font-family: inherit;
}

.template-content-box {
  max-height: 200px;
  overflow-y: auto;
}

.var-inputs {
  padding: 12px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
}

.var-input-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
}

.var-input-label {
  font-size: 12px;
  color: #1890ff;
  font-family: Consolas, Monaco, monospace;
  white-space: nowrap;
  min-width: 80px;
}

.preview-card {
  border-radius: 8px;
  background: #fafafa;
  height: 100%;
}

.preview-title-box {
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  margin-bottom: 12px;
  min-height: 36px;
  word-break: break-all;
}

.preview-title-content {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.6;
}

.preview-content-box {
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  min-height: 80px;
  max-height: 400px;
  overflow-y: auto;
}

.preview-placeholder {
  font-size: 13px;
  color: #bbb;
  font-style: italic;
}

.step-actions {
  margin-top: 24px;
  text-align: center;
}
</style>
